import React, { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PantallaCompleta } from "@/components/control/PantallaCompleta";
import { ExportExcelButton } from "@/components/ExportExcelButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { calcEjercicios, GF_DEF, GP_DEF } from "@/lib/gastoFijo";
import { paletaVars } from "@/lib/gastoFijoPaletas";
import { semanasProyeccion, filasProyeccion } from "@/lib/proyeccionMensual";
import { ProyeccionTabla } from "@/components/control/ProyeccionTabla";
import { ReporteAnual } from "@/components/control/ReporteAnual";
import { Aviso, MesPicker } from "@/pages/GastoFijo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const card = "bg-white rounded-2xl border border-slate-200/70 shadow-sm";
const Spin = () => <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;

const useApi = (key, url, params, pick = (d) => d) => useQuery({ queryKey: key, queryFn: async () => pick((await api.get(url, { params })).data) });
const items = (d) => d.items;
const MESES_L = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function Reporte({ planta, structure, ym, setYm, vista }) {
  const [fs, setFs] = useState(false);
  const cerrar = useCallback(() => setFs(false), []);
  const p = { planta, structure, year: ym.year, month: ym.month };
  const gp = useApi([GP_DEF.mod, "ejercicios", planta, ym.year, ym.month], "/control-semanal/gestion-planta/ejercicios", p, items);
  const gf = useApi([GF_DEF.mod, "ejercicios", planta, ym.year, ym.month], "/control-semanal/gasto-fijo/ejercicios", p, items);
  const vars = useApi(["gestion-planta", "variables", planta, ym.year, ym.month], "/control-semanal/gestion-planta/variables", p);
  const precios = useApi(["proyeccion-mensual", "precios", planta, ym.year, ym.month], "/control-semanal/proyeccion-mensual/precios", p);
  const listo = gp.data && gf.data && vars.data && precios.data;
  let body = <Spin />;
  let reportes = null;
  if (listo) {
    const { filas, ventaMes } = semanasProyeccion(ym, vars.data.valores, precios.data.semanas);
    const ctx = { ventaMes, ym, gp: calcEjercicios(gp.data, GP_DEF), gf: calcEjercicios(gf.data, GF_DEF), vars: vars.data.valores };
    const rows = filasProyeccion({ filas, ...ctx });
    const todos = [
      { key: "proyeccion", label: "Proyección mensual", node: <ProyeccionTabla filas={filas} ventaMes={ventaMes} rows={rows} ym={ym} /> },
    ];
    reportes = todos.filter((r) => VISTAS[vista].tabs.includes(r.key));
    body = reportes.length === 1 ? reportes[0].node : (
      <Tabs defaultValue={reportes[0].key} data-testid="pm-tabs">
        <TabsList>
          {reportes.map((r) => <TabsTrigger key={r.key} value={r.key} data-testid={`pm-tab-${r.key}`}>{r.label}</TabsTrigger>)}
        </TabsList>
        {reportes.map((r) => <TabsContent key={r.key} value={r.key} className="mt-4">{r.node}</TabsContent>)}
      </Tabs>
    );
  }
  return (
    <div className="space-y-4" data-testid={`pm-planta-${planta}`}>
      <div className={`${card} p-4 flex flex-wrap items-center gap-3`}>
        <MesPicker ym={ym} setYm={setYm} />
        <Button variant="outline" className="ml-auto" disabled={!reportes} onClick={() => setFs(true)} data-testid="pm-pantalla-completa"><Maximize2 className="mr-2 h-4 w-4" />Pantalla completa</Button>
        {reportes && <ExportExcelButton testid="pm-export" fileName={`${VISTAS[vista].archivo}_${planta}_${ym.year}_${String(ym.month).padStart(2, "0")}`}
          titulo={`${planta} · ${MESES_L[ym.month - 1]} ${ym.year}`} hojas={reportes.map((r) => ({ name: r.label, node: r.node }))} />}
      </div>
      {fs && reportes && <PantallaCompleta titulo={`${planta} · ${MESES_L[ym.month - 1]} ${ym.year}`} reportes={reportes} onClose={cerrar} />}
      <div className={`${card} p-5 space-y-4`}>
        <p className="text-center rounded-lg bg-[var(--gf-title)] py-1.5 text-lg font-bold text-white" data-testid="pm-planta-titulo">{planta}</p>
        {body}
        <p className="text-xs text-slate-500" data-testid="pm-fuentes">
          Fuentes: Venta kilos, Comisiones, Descuentos y HGN de Gestión Planta (Variables de Gestión) · Presupuesto, Extraordinarios y Provisiones de Gestión Planta · Bloque 3 de Gasto Fijo · P.V. de Precios CNE (ponderado) · P.C. de Precios de compra (Σ precio por kg × %) · Flete de Precios de flete (Flete total).
        </p>
      </div>
    </div>
  );
}

const VISTAS = {
  control: { titulo: "Reporte Control Semanal", archivo: "Reporte_Control_Semanal", tabs: [], anual: true,
    desc: "Corrida semanal anual por semana completa y comparativo Plan contra Real." },
  control_v2: { titulo: "Reporte Control Semanal V2", archivo: "Reporte_Control_Semanal_V2", tabs: [], anual: true,
    desc: "Corrida semanal anual por semana completa y comparativo Plan contra Real." },
  proyeccion: { titulo: "Proyección Mensual", archivo: "Proyeccion_Mensual", tabs: ["proyeccion"],
    desc: "Reporte mensual por semana (real y proyección) con la información de los módulos existentes." },
};

export default function ProyeccionMensual({ vista = "control" }) {
  const { structureKey, node, currentStructure } = useGlobal();
  const { data: cfg } = useQuery({ queryKey: ["gestion-planta", "config"], queryFn: async () => (await api.get("/control-semanal/gestion-planta/config")).data });
  const [ymSel, setYm] = useState(null);
  const ym = ymSel || (cfg ? { year: cfg.year, month: cfg.month } : null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["reporte", "nodo", structureKey, node?.id ?? "entire"],
    queryFn: async () => (await api.get("/control-semanal/reporte/nodo", { params: { structure: structureKey, node_id: node?.id } })).data,
    enabled: !!structureKey,
  });
  let body;
  if (isLoading || !ym) body = <Spin />;
  else if (error) body = <Aviso testid="pm-error" text={formatApiErrorDetail(error?.response?.data?.detail) || "No se pudo cargar el nodo seleccionado."} />;
  else if (!data?.is_leaf) body = <Aviso testid="pm-no-hoja" text={`${VISTAS[vista].titulo} está disponible únicamente a nivel de planta o sucursal. Seleccione una planta o sucursal desde la estructura organizacional para visualizar su información.`} />;
  else if (VISTAS[vista].anual) body = (
    <Tabs defaultValue="corrida" data-testid="pm-tabs">
      <TabsList>
        <TabsTrigger value="corrida" data-testid="pm-tab-corrida">Corrida semanal</TabsTrigger>
        <TabsTrigger value="comparativo" data-testid="pm-tab-comparativo">Comparativo semanal</TabsTrigger>
      </TabsList>
      <TabsContent value="corrida" className="mt-4"><ReporteAnual key={data.planta} planta={data.planta} structure={structureKey} cfg={cfg} semaforo={vista === "control_v2"} /></TabsContent>
      <TabsContent value="comparativo" className="mt-4"><ReporteAnual key={data.planta} planta={data.planta} structure={structureKey} cfg={cfg} modo="comparativo" semaforo={vista === "control_v2"} /></TabsContent>
    </Tabs>
  );
  else body = <Reporte key={data.planta} planta={data.planta} structure={structureKey} ym={ym} setYm={setYm} vista={vista} />;
  return (
    <div className="w-full space-y-6" data-testid={{ proyeccion: "proyeccion-mensual-rpt", control_v2: "reporte-control-v2" }[vista] || "proyeccion-mensual"} style={paletaVars()} data-export-root>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Control Semanal</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">{VISTAS[vista].titulo}</h1>
        <p className="text-sm text-slate-500 mt-1">{VISTAS[vista].desc} {currentStructure?.name ? `Estructura: ${currentStructure.name}${node ? ` · ${node.name}` : " · Toda la estructura"}` : ""}</p>
      </div>
      {body}
    </div>
  );
}
